import type { QueryHookOptions } from '@apollo/client';
import type { GetMeQuery, GetMeQueryVariables } from 'generated/graphql';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { CurrentUserQueryContext } from './CurrentUserQueryContext';
import { CurrentUserQueryProvider, useCurrentUserQuery } from './contextedQuery';

interface CurrentUserProviderProps {
  children: ReactNode;
  queryOptions?: Omit<QueryHookOptions<GetMeQuery, GetMeQueryVariables>, 'skip'>;
}

interface DeferredQuery {
  promise: Promise<void>;
  resolve: () => void;
}

export const CurrentUserProvider = ({ children, queryOptions }: CurrentUserProviderProps) => {
  return (
    <CurrentUserQueryProvider options={queryOptions}>
      <CurrentUserQueryBridge>{children}</CurrentUserQueryBridge>
    </CurrentUserQueryProvider>
  );
};

CurrentUserProvider.displayName = 'CurrentUserProvider';

const CurrentUserQueryBridge = ({ children }: { children: ReactNode }) => {
  const query = useCurrentUserQuery();
  const deferredQueryRef = useRef<DeferredQuery>();

  if (query.data == null && query.error == null && deferredQueryRef.current == null) {
    deferredQueryRef.current = createDeferredQuery();
  }

  const suspensePromise = deferredQueryRef.current?.promise;

  useEffect(() => {
    if (query.data == null && query.error == null) return;

    deferredQueryRef.current?.resolve();
    deferredQueryRef.current = undefined;
  }, [query.data, query.error]);

  const contextValue = useMemo(() => ({ query, suspensePromise }), [query, suspensePromise]);

  return (
    <CurrentUserQueryContext.Provider value={contextValue}>
      {children}
    </CurrentUserQueryContext.Provider>
  );
};

function createDeferredQuery(): DeferredQuery {
  let resolvePromise: (() => void) | undefined;
  const promise = new Promise<void>((resolve) => {
    resolvePromise = resolve;
  });

  return {
    promise,
    resolve: () => resolvePromise?.(),
  };
}
